import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from "firebase/firestore";

import { db } from "../firebase";
import "./AdminMembers.css";

function AdminMembers() {
  const navigate = useNavigate();

  const [storeMembers, setStoreMembers] = useState([]);
  const [cards, setCards] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [transactions, setTransactions] = useState([]);

  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [adjustStore, setAdjustStore] = useState(null);
  const [adjustType, setAdjustType] = useState("increase");
  const [adjustPoints, setAdjustPoints] = useState("");
  const [adjustReason, setAdjustReason] = useState("Admin adjustment");
  const [adjustSaving, setAdjustSaving] = useState(false);

  useEffect(() => {
    const membersRef = collection(db, "storeMembers");
    const membersQuery = query(membersRef, orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      membersQuery,
      (snapshot) => {
        const list = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setStoreMembers(list);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching store members:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const cardsRef = collection(db, "cards");

    const unsubscribe = onSnapshot(
      cardsRef,
      (snapshot) => {
        const list = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setCards(list);
      },
      (error) => {
        console.error("Error fetching cards:", error);
      }
    );

    return () => unsubscribe();
  }, []);

  const cardsMap = useMemo(() => {
    const map = {};

    cards.forEach((card) => {
      map[card.id] = card;
    });

    return map;
  }, [cards]);

  const groupedMembers = useMemo(() => {
    const memberMap = {};

    storeMembers.forEach((member) => {
      const cardNumber = member.cardNumber || member.id;
      const cardInfo = cardsMap[cardNumber];

      if (!memberMap[cardNumber]) {
        memberMap[cardNumber] = {
          cardNumber,
          name: member.name || "Guest",
          email: member.email || "Not available",
          totalPoints: 0,
          storeCount: 0,
          joinedAt: member.createdAt,
          stores: [],
          cardInfo: cardInfo || null,
        };
      }

      memberMap[cardNumber].totalPoints += Number(member.points || 0);
      memberMap[cardNumber].storeCount += 1;

      memberMap[cardNumber].stores.push({
        documentId: member.id,
        storeId: member.storeId || "Not available",
        storeName: member.storeName || "Not available",
        points: Number(member.points || 0),
        joinedAt: member.createdAt,
      });

      if (cardInfo) {
        memberMap[cardNumber].cardInfo = cardInfo;
      }
    });

    return Object.values(memberMap);
  }, [storeMembers, cardsMap]);

  const filteredMembers = useMemo(() => {
    if (!searchText.trim()) return groupedMembers;

    const search = searchText.toLowerCase();

    return groupedMembers.filter((member) => {
      const name = member.name?.toLowerCase() || "";
      const email = member.email?.toLowerCase() || "";
      const cardNumber = member.cardNumber?.toLowerCase() || "";

      return (
        name.includes(search) ||
        email.includes(search) ||
        cardNumber.includes(search)
      );
    });
  }, [groupedMembers, searchText]);

  useEffect(() => {
    if (!selectedMember?.cardNumber) {
      setTransactions([]);
      return;
    }

    setHistoryLoading(true);

    const transactionsRef = collection(db, "transactions");
    const transactionsQuery = query(
      transactionsRef,
      where("cardNumber", "==", selectedMember.cardNumber)
    );

    const unsubscribe = onSnapshot(
      transactionsQuery,
      (snapshot) => {
        const list = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const sortedList = list.sort((a, b) => {
          const dateA = a.timestamp?.toDate
            ? a.timestamp.toDate()
            : new Date(0);

          const dateB = b.timestamp?.toDate
            ? b.timestamp.toDate()
            : new Date(0);

          return dateB - dateA;
        });

        setTransactions(sortedList);
        setHistoryLoading(false);
      },
      (error) => {
        console.error("Error fetching transactions:", error);
        setTransactions([]);
        setHistoryLoading(false);
      }
    );

    return () => unsubscribe();
  }, [selectedMember]);

  const formatDate = (dateValue) => {
    if (!dateValue) return "Not available";

    try {
      const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);

      return date.toLocaleDateString("en-CA", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "Not available";
    }
  };

  const formatDateTime = (dateValue) => {
    if (!dateValue) return "Not available";

    try {
      const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);

      return date.toLocaleString("en-CA", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Not available";
    }
  };

  const getTransactionPoints = (transaction) => {
    const points = Number(transaction.points || 0);
    const type = transaction.type?.toLowerCase() || "";

    if (
      type.includes("redeem") ||
      type.includes("decrease") ||
      type.includes("subtract")
    ) {
      return -Math.abs(points);
    }

    return points;
  };

  const openAdjustModal = (store, type) => {
    setAdjustStore(store);
    setAdjustType(type);
    setAdjustPoints("");
    setAdjustReason("Admin adjustment");
  };

  const closeAdjustModal = () => {
    setAdjustStore(null);
    setAdjustPoints("");
    setAdjustReason("Admin adjustment");
    setAdjustSaving(false);
  };

  const handleAdjustPoints = async (e) => {
    e.preventDefault();

    if (!selectedMember || !adjustStore) {
      alert("Please select a member and store first.");
      return;
    }

    const pointsValue = Number(adjustPoints);

    if (!pointsValue || pointsValue <= 0) {
      alert("Please enter points greater than 0.");
      return;
    }

    try {
      setAdjustSaving(true);

      const memberRef = doc(db, "storeMembers", adjustStore.documentId);
      const transactionRef = doc(collection(db, "transactions"));

      await runTransaction(db, async (transaction) => {
        const memberSnap = await transaction.get(memberRef);

        if (!memberSnap.exists()) {
          throw new Error("Member not found.");
        }

        const memberData = memberSnap.data();
        const currentPoints = Number(memberData.points || 0);

        let newBalance = currentPoints;

        if (adjustType === "increase") {
          newBalance = currentPoints + pointsValue;
        } else {
          newBalance = currentPoints - pointsValue;
        }

        if (newBalance < 0) {
          throw new Error("Points cannot go below 0.");
        }

        transaction.update(memberRef, {
          points: newBalance,
          updatedAt: serverTimestamp(),
        });

        transaction.set(transactionRef, {
          balance: newBalance,
          cardNumber: selectedMember.cardNumber,
          memberName: memberData.name || selectedMember.name,
          points: pointsValue,
          reason: adjustReason.trim(),
          source: "admin",
          storeId: adjustStore.storeId,
          storeMemberId: adjustStore.documentId,
          storeName: adjustStore.storeName,
          timestamp: serverTimestamp(),
          type: adjustType === "increase" ? "admin_add" : "admin_decrease",
        });
      });

      closeAdjustModal();
      alert("Points updated successfully.");
    } catch (error) {
      console.error("Error adjusting points:", error);
      alert(error.message || "Something went wrong while updating points.");
    } finally {
      setAdjustSaving(false);
    }
  };

  return (
    <div className="members-page">
      <div className="members-topbar">
        <div>
          <h1>All Store Members</h1>
          <p>
            Search customers, check card details, points, stores, and full
            history.
          </p>
        </div>

        <button onClick={() => navigate("/dashboard")}>
          Back to Dashboard
        </button>
      </div>

      <div className="members-layout">
        <div className="members-list-panel">
          <div className="members-summary">
            <div>
              <p>Total Unique Members</p>
              <h2>{groupedMembers.length}</h2>
            </div>

            <div>
              <p>Total Store Memberships</p>
              <h2>{storeMembers.length}</h2>
            </div>
          </div>

          <input
            className="member-search"
            type="text"
            placeholder="Search by name, email, or card number..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          {loading ? (
            <div className="empty-member-box">Loading members...</div>
          ) : filteredMembers.length === 0 ? (
            <div className="empty-member-box">No members found.</div>
          ) : (
            <div className="members-list">
              {filteredMembers.map((member) => (
                <button
                  key={member.cardNumber}
                  className={
                    selectedMember?.cardNumber === member.cardNumber
                      ? "member-card selected"
                      : "member-card"
                  }
                  onClick={() => setSelectedMember(member)}
                >
                  <div>
                    <h3>{member.name}</h3>
                    <p>{member.email}</p>
                    <span>Card: {member.cardNumber}</span>
                  </div>

                  <div className="member-points">
                    <strong>{member.totalPoints}</strong>
                    <span>pts</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="member-detail-panel">
          {!selectedMember ? (
            <div className="select-member-box">
              Select a member to view full details.
            </div>
          ) : (
            <>
              <div className="detail-header">
                <div>
                  <h2>{selectedMember.name}</h2>
                  <p>{selectedMember.email}</p>
                </div>

                <div className="big-points">
                  <strong>{selectedMember.totalPoints}</strong>
                  <span>Total Points</span>
                </div>
              </div>

              <div className="detail-grid">
                <div className="detail-box">
                  <p>Card Number</p>
                  <h3>{selectedMember.cardNumber}</h3>
                </div>

                <div className="detail-box">
                  <p>Stores Joined</p>
                  <h3>{selectedMember.storeCount}</h3>
                </div>

                <div className="detail-box">
                  <p>First Joined</p>
                  <h3>{formatDate(selectedMember.joinedAt)}</h3>
                </div>
              </div>

              <div className="section-card">
                <h3>Card Activation Details</h3>

                <div className="detail-grid">
                  <div className="detail-box">
                    <p>Card Status</p>
                    <h3>
                      {selectedMember.cardInfo?.activated
                        ? "Activated"
                        : "Not Activated"}
                    </h3>
                  </div>

                  <div className="detail-box">
                    <p>Activated By Store</p>
                    <h3>
                      {selectedMember.cardInfo?.activatedByStoreId ||
                        "Not available"}
                    </h3>
                  </div>

                  <div className="detail-box">
                    <p>Activated At</p>
                    <h3>{formatDate(selectedMember.cardInfo?.activatedAt)}</h3>
                  </div>
                </div>
              </div>

              <div className="section-card">
                <h3>Stores Where This Member Exists</h3>

                <div className="table-wrapper">
                  <table className="member-table">
                    <thead>
                      <tr>
                        <th>Store ID</th>
                        <th>Store Name</th>
                        <th>Points</th>
                        <th>Joined Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {selectedMember.stores.map((store) => (
                        <tr key={store.documentId}>
                          <td>{store.storeId}</td>
                          <td>{store.storeName}</td>
                          <td>{store.points}</td>
                          <td>{formatDate(store.joinedAt)}</td>
                          <td>
                            <div className="points-action-buttons">
                              <button
                                className="increase-points-btn"
                                onClick={() =>
                                  openAdjustModal(store, "increase")
                                }
                              >
                                + Increase
                              </button>

                              <button
                                className="decrease-points-btn"
                                onClick={() =>
                                  openAdjustModal(store, "decrease")
                                }
                              >
                                - Decrease
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="section-card">
                <h3>Transaction History</h3>

                {historyLoading ? (
                  <div className="empty-member-box">Loading history...</div>
                ) : transactions.length === 0 ? (
                  <div className="empty-member-box">
                    No transaction history found.
                  </div>
                ) : (
                  <div className="table-wrapper">
                    <table className="member-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Store</th>
                          <th>Member Name</th>
                          <th>Type</th>
                          <th>Points</th>
                          <th>Balance</th>
                          <th>Store Member ID</th>
                        </tr>
                      </thead>

                      <tbody>
                        {transactions.map((transaction) => {
                          const transactionPoints =
                            getTransactionPoints(transaction);

                          return (
                            <tr key={transaction.id}>
                              <td>{formatDateTime(transaction.timestamp)}</td>
                              <td>
                                {transaction.storeName ||
                                  transaction.storeId ||
                                  "Not available"}
                              </td>
                              <td>
                                {transaction.memberName || "Not available"}
                              </td>
                              <td>{transaction.type || "Not available"}</td>
                              <td
                                className={
                                  transactionPoints >= 0
                                    ? "positive-points"
                                    : "negative-points"
                                }
                              >
                                {transactionPoints >= 0 ? "+" : ""}
                                {transactionPoints}
                              </td>
                              <td>{transaction.balance ?? "Not available"}</td>
                              <td>
                                {transaction.storeMemberId || "Not available"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {adjustStore && selectedMember && (
        <div className="adjust-modal-backdrop">
          <div className="adjust-modal">
            <h2>
              {adjustType === "increase"
                ? "Increase Points"
                : "Decrease Points"}
            </h2>

            <p>
              Store: <strong>{adjustStore.storeName}</strong>
            </p>

            <p>
              Member: <strong>{selectedMember.name}</strong>
            </p>

            <form onSubmit={handleAdjustPoints}>
              <div className="adjust-form-group">
                <label>Points</label>
                <input
                  type="number"
                  min="1"
                  value={adjustPoints}
                  onChange={(e) => setAdjustPoints(e.target.value)}
                  placeholder="Enter points"
                />
              </div>

              <div className="adjust-form-group">
                <label>Reason</label>
                <input
                  type="text"
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  placeholder="Example: Manual correction"
                />
              </div>

              <div className="adjust-modal-actions">
                <button
                  type="button"
                  className="cancel-adjust-btn"
                  onClick={closeAdjustModal}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className={
                    adjustType === "increase"
                      ? "confirm-increase-btn"
                      : "confirm-decrease-btn"
                  }
                  disabled={adjustSaving}
                >
                  {adjustSaving ? "Saving..." : "Confirm"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminMembers;